import { ExternalLink, Mail } from 'lucide-react'
import { Fragment, useState } from 'react'
import { toast } from 'sonner'

import type { Invoice, RegionId } from '@snootsvet/shared'
import {
  formatCurrency,
  formatInstantDate,
  postSendInvoicePaymentLinkBodySchema,
  resolveDateFormat,
} from '@snootsvet/shared'

import { Button } from '@/frontend/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/frontend/components/ui/dialog'
import { Input } from '@/frontend/components/ui/input'
import { Label } from '@/frontend/components/ui/label'
import { InvoiceStatusBadge } from '@/frontend/features/billing/invoice-status-badge'
import { useSendInvoicePaymentLink } from '@/frontend/hooks/api/patients/use-send-invoice-payment-link'
import { cn } from '@/frontend/lib/utils'

/** Grid of invoice rows with status dots, amounts, badges, and actions. */
export function InvoiceList({
  invoices,
  patientId,
  memberEmail,
  memberRegion,
  showStatus = true,
}: {
  invoices: Invoice[]
  patientId: string
  memberEmail: string
  memberRegion: RegionId
  showStatus?: boolean
}) {
  const dateFormat = resolveDateFormat(null, memberRegion)

  return (
    <div
      className={cn(
        'grid items-center gap-x-2 gap-y-1',
        showStatus ? 'grid-cols-[auto_1fr_auto_auto_auto]' : 'grid-cols-[auto_1fr_auto_auto]'
      )}
    >
      {invoices.map((invoice) => (
        <Fragment key={invoice.id}>
          <span
            className={`size-1.5 rounded-full ${
              invoice.overdue
                ? 'bg-destructive-accent'
                : invoice.status === 'paid'
                  ? 'bg-success-accent'
                  : invoice.status === 'open'
                    ? 'bg-pending-accent'
                    : 'bg-muted-foreground'
            }`}
          />
          <span className="text-sm font-medium text-muted-foreground">
            {formatInstantDate(invoice.issuedAt, dateFormat)}
          </span>
          <span className="text-right text-sm font-semibold text-muted-foreground tabular-nums">
            {formatCurrency(invoice.amountDue, invoice.currency)}
          </span>
          {showStatus && (
            <div className="justify-self-center">
              <InvoiceStatusBadge invoice={invoice} size="xs" />
            </div>
          )}
          <PatientInvoiceActions
            invoice={invoice}
            patientId={patientId}
            memberEmail={memberEmail}
          />
        </Fragment>
      ))}
    </div>
  )
}

/** Filtered view showing only overdue invoices with a summary header. */
export function PatientMembershipOverdueInvoices({
  invoices,
  patientId,
  memberEmail,
  memberRegion,
}: {
  invoices: Invoice[]
  patientId: string
  memberEmail: string
  memberRegion: RegionId
}) {
  const overdue = invoices.filter((invoice) => invoice.overdue)

  if (overdue.length === 0) return null

  const totalDue = overdue.reduce((sum, invoice) => sum + invoice.amountDue, 0)

  return (
    <div className="space-y-1.5 text-sm">
      <p className="flex items-center gap-2 font-medium">
        {overdue.length} overdue {overdue.length === 1 ? 'invoice' : 'invoices'}
        <span className="ml-auto tabular-nums">
          {formatCurrency(totalDue, overdue[0].currency)}
        </span>
      </p>
      <InvoiceList
        invoices={overdue}
        patientId={patientId}
        memberEmail={memberEmail}
        memberRegion={memberRegion}
        showStatus={false}
      />
    </div>
  )
}

/** Stripe dashboard and payment-link actions for an invoice. */
function PatientInvoiceActions({
  invoice,
  patientId,
  memberEmail,
}: {
  invoice: Invoice
  patientId: string
  memberEmail: string
}) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [recipientEmail, setRecipientEmail] = useState(memberEmail)
  const sendLink = useSendInvoicePaymentLink(patientId)
  const amount = formatCurrency(invoice.amountDue, invoice.currency)
  const outstanding = invoice.status === 'open' && invoice.amountDue > 0
  const trimmedEmail = recipientEmail.trim()
  const emailValid = postSendInvoicePaymentLinkBodySchema.safeParse({ email: trimmedEmail }).success

  function handleConfirmOpenChange(nextOpen: boolean) {
    if (!nextOpen && sendLink.isPending) return
    if (nextOpen) setRecipientEmail(memberEmail)
    setConfirmOpen(nextOpen)
  }

  function handleSend() {
    sendLink.mutate(
      { invoiceId: invoice.id, email: trimmedEmail },
      {
        onSuccess: (response) => {
          toast.success(`Payment link sent to ${response.email}`)
          setConfirmOpen(false)
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Failed to send payment link')
        },
      }
    )
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-0.5">
        <Button
          variant="ghost"
          size="icon-xs"
          render={
            <a href={invoice.dashboardInvoiceUrl} target="_blank" rel="noopener noreferrer" />
          }
          aria-label="View invoice in Stripe"
        >
          <ExternalLink className="size-3.5" />
        </Button>
        {outstanding ? (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => handleConfirmOpenChange(true)}
            aria-label="Email payment link to member"
          >
            <Mail className="size-3.5" />
          </Button>
        ) : (
          <span />
        )}
      </div>

      {outstanding && (
        <Dialog open={confirmOpen} onOpenChange={handleConfirmOpenChange}>
          <DialogContent nested>
            <form
              onSubmit={(event) => {
                event.preventDefault()
                if (emailValid) handleSend()
              }}
              className="space-y-4"
            >
              <DialogHeader>
                <DialogTitle>Send payment link?</DialogTitle>
                <DialogDescription>
                  Email a link to pay {amount}. Pre-filled with the member&apos;s email - change it
                  to send the link to a different address.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5">
                <Label htmlFor={`recipient-${invoice.id}`}>Send to</Label>
                <Input
                  id={`recipient-${invoice.id}`}
                  type="email"
                  value={recipientEmail}
                  onChange={(event) => setRecipientEmail(event.target.value)}
                  placeholder="name@example.com"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmOpen(false)}
                  disabled={sendLink.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={sendLink.isPending || !emailValid}>
                  {sendLink.isPending ? 'Sending…' : 'Send'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
